import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  LinearProgress,
  Button,
  Chip,
  IconButton,
  Tooltip,
  useMediaQuery,
} from '@mui/material';
import {
  CalendarMonth as CalendarIcon,
  LocationOn as LocationIcon,
  ChevronRight as ChevronRightIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { Incident } from '../../Types/Incident';
import IncidentStatusChip from './IncidentStatusChip';
import IncidentElapsedTimer from './IncidentElapsedTimer';
import IncidentIcon from './IncidentIcon';
import { appColors } from '../../Config/Theme';
import { getProgressBarColor } from '../../Utils/incidentDashboardState';
import { formatDateTime } from '../../Utils/dateHelpers';
import { isIncidentClosed, getIncidentOperationalState, IncidentOperationalState } from '../../Utils/incidentHelpers';
import HeadcountTeamSummary from './HeadcountTeamSummary';
import { useSecurity } from '../../Contexts/SecurityProvider';
import { getIncidentScopeCounts, getNeedsAttentionCount } from '../../features/incidents/accountabilityScope';

interface IncidentRowProps {
  incident: Incident;
  compact?: boolean;
  now?: number;
  onEdit?: (incident: Incident) => void;
}

/** Defensively extract counts, defaulting to 0 if missing. */
const getIncidentCounts = (incident: Incident) => {
  const scope = getIncidentScopeCounts(incident);
  const ac = scope.accountabilityCounts;
  return {
    total: ac.total,
    accounted: ac.accounted,
    notAccounted: getNeedsAttentionCount(scope),
    notChecked: ac.notChecked,
    percentComplete: ac.percentComplete,
    fullRosterTotal: scope.fullRosterCounts.total,
    assignedOfficeNoBadge: scope.assignedOfficeNoBadgeTodayNotChecked ?? 0,
  };
};

/** Tabular number style applied to all metric values */
const TABULAR = { fontVariantNumeric: 'tabular-nums' } as const;

const StatCell: React.FC<{ label: string; value: number; color: string; overrideLabel?: string }> = ({ label, value, color, overrideLabel }) => (
  <Box sx={{ textAlign: 'center', width: '100%' }}>
    <Typography
      variant="subtitle2"
      sx={{
        color: appColors.text.caption,
        fontWeight: 700,
        fontSize: { xs: '0.82rem', md: '0.92rem' },
        lineHeight: 1.22,
        letterSpacing: 0.03,
        mb: 0.12,
      }}
    >
      {overrideLabel || label}
    </Typography>
    <Typography
      sx={{
        fontWeight: 800,
        color,
        lineHeight: 1.1,
        fontSize: { xs: '1.08rem', md: '1.18rem' },
        ...TABULAR,
      }}
    >
      {value}
    </Typography>
  </Box>
);

// ── Operational state visual config ──
interface StateConfig {
  label: string;
  shortLabel: string;
  chipColor: string;
  chipBg: string;
  chipBorder: string;
  actionLabel: string;
  getActionRoute: (id: string) => string;
  actionPermission?: string;
}

const STATE_CONFIG: Record<Exclude<IncidentOperationalState, 'closed'>, StateConfig> = {
  needsAttention: {
    label: 'Not Accounted',
    shortLabel: 'Not Accounted',
    chipColor: appColors.status.error,
    chipBg: 'rgba(211, 47, 47, 0.06)',
    chipBorder: 'rgba(211, 47, 47, 0.35)',
    actionLabel: 'Review Not Accounted',
    getActionRoute: (id) => `/incidents/${id}/unaccounted`,
    actionPermission: 'Incident.ViewUnaccounted',
  },
  inProgress: {
    label: 'Counting in Progress',
    shortLabel: 'In Progress',
    chipColor: appColors.primary.main,
    chipBg: 'rgba(46, 139, 132, 0.08)',
    chipBorder: appColors.primary.main,
    actionLabel: 'Continue Headcount',
    getActionRoute: (id) => `/incidents/${id}/headcount`,
  },
  notStarted: {
    label: 'Counting not Started',
    shortLabel: 'Not Started',
    chipColor: appColors.text.body,
    chipBg: appColors.surface.inactive,
    chipBorder: appColors.border.subtle,
    actionLabel: 'Start Headcount',
    getActionRoute: (id) => `/incidents/${id}/views`,
  },
  complete: {
    label: 'Complete',
    shortLabel: 'Complete',
    chipColor: appColors.status.success,
    chipBg: appColors.status.successBg,
    chipBorder: appColors.status.successBorder,
    actionLabel: 'Open Dashboard',
    getActionRoute: (id) => `/incidents/${id}/incidentdashboard`,
  },
  emptyRoster: {
    label: 'Empty Roster',
    shortLabel: 'Empty Roster',
    chipColor: appColors.text.caption,
    chipBg: appColors.surface.inactive,
    chipBorder: appColors.border.subtle,
    actionLabel: 'Open Dashboard',
    getActionRoute: (id) => `/incidents/${id}/incidentdashboard`,
  },
};

/** Build the summary line as styled spans */
const SummaryLine: React.FC<{ counts: ReturnType<typeof getIncidentCounts>; opState: IncidentOperationalState }> = ({ counts, opState }) => {
  const parts: React.ReactNode[] = [];

  if (opState === 'needsAttention') {
    if (counts.notAccounted > 0) {
      parts.push(
        <Typography
          key="na"
          component="span"
          variant="caption"
          sx={{ color: appColors.status.error, fontWeight: 600, ...TABULAR }}
        >
          {counts.notAccounted} not accounted
        </Typography>,
      );
    }
  } else if (opState === 'inProgress') {
    parts.push(
      <Typography key="ac" component="span" variant="caption" sx={{ color: appColors.text.caption, ...TABULAR }}>
        {counts.accounted} accounted
      </Typography>,
    );
  } else if (opState === 'notStarted') {
    parts.push(
      <Typography key="ns" component="span" variant="caption" sx={{ color: appColors.text.caption, ...TABULAR }}>
        {counts.total} accountability total
      </Typography>,
    );
  } else if (opState === 'complete') {
    parts.push(
      <Typography key="done" component="span" variant="caption" sx={{ color: appColors.status.success }}>
        Everyone has been checked
      </Typography>,
    );
  } else if (opState === 'emptyRoster') {
    parts.push(
      <Typography key="empty" component="span" variant="caption" sx={{ color: appColors.text.caption }}>
        No roster data available
      </Typography>,
    );
  }

  if (parts.length === 0) return null;
  return <Box sx={{ mt: 0.25, display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>{parts}</Box>;
};

const IncidentRow: React.FC<IncidentRowProps> = ({ incident, compact = false, now, onEdit }) => {
  const navigate = useNavigate();
  const { hasPermission } = useSecurity();
  const compactLabels = useMediaQuery('(max-width: 1535px)');
  const isActive = !isIncidentClosed(incident);
  const counts = getIncidentCounts(incident);
  const pct = Math.round(counts.percentComplete);
  const opState = getIncidentOperationalState(incident);
  const progressColor = getProgressBarColor(opState, pct);
  const dateLabel = isActive
    ? `Started ${formatDateTime(incident.startedAtUtc)}`
    : `Ended ${incident.closedAtUtc ? formatDateTime(incident.closedAtUtc) : 'N/A'}`;

  const notAccountedColor = counts.notAccounted > 0 ? appColors.status.error : appColors.text.caption;
  const hasTeam = incident.incidentCommanders.length > 0 || incident.headcounters.length > 0;
  const config = opState !== 'closed' ? STATE_CONFIG[opState] : null;

  // Permission-gate the action button for needsAttention
  const canSeeAction = config
    ? !config.actionPermission || hasPermission(config.actionPermission)
    : false;

  const handleCardClick = () => navigate(`/incidents/${incident.incidentId}/incidentdashboard`);
  const handleAction = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (config) navigate(config.getActionRoute(incident.incidentId));
  };

  // ── Past / compact card ──
  if (compact || !isActive) {
    return (
      <Box
        onClick={handleCardClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleCardClick(); } }}
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'center' },
          bgcolor: appColors.surface.card,
          borderRadius: '10px',
          border: `1px solid ${appColors.border.light}`,
          px: { xs: 1, sm: 2 },
          py: { xs: 1, sm: 1.25 },
          mb: 0.75,
          cursor: 'pointer',
          transition: 'box-shadow 0.15s, border-color 0.15s',
          '&:hover': { boxShadow: appColors.shadow.rowHover, borderColor: appColors.border.hover },
          '&:focus-visible': { outline: `2px solid ${appColors.primary.main}`, outlineOffset: 2 },
          gap: { xs: 0.5, sm: 1.5 },
          minWidth: 0,
        }}
      >
        <IncidentIcon incidentType={incident.incidentType} />
        <Box sx={{ flex: '1 1 0', minWidth: 0, mr: { xs: 0, sm: 1 }, mb: { xs: 0.5, sm: 0 } }}>
          <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.3, color: appColors.text.heading }}>
            {incident.incidentName}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.25, flexWrap: 'wrap' }}>
            {incident.location && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                <LocationIcon sx={{ fontSize: 12, color: appColors.text.caption }} />
                <Typography variant="caption" sx={{ color: appColors.text.caption }}>{incident.location}</Typography>
              </Box>
            )}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
              <CalendarIcon sx={{ fontSize: 12, color: appColors.text.placeholder }} />
              <Typography variant="caption" sx={{ color: appColors.text.placeholder }}>{dateLabel}</Typography>
            </Box>
          </Box>
        </Box>

        {/* Compact accountability */}
        {counts.total > 0 ? (
          <Box sx={{
            display: { xs: 'block', sm: 'block' },
            minWidth: { xs: 0, sm: 210 },
            maxWidth: { xs: '100%', sm: 240 },
            mt: { xs: 1, sm: 0 },
          }}>
            <Typography variant="caption" sx={{ color: appColors.text.caption, fontWeight: 500 }}>
              Accountability
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
              <Typography
                sx={{ fontWeight: 700, color: progressColor, lineHeight: 1.3, fontSize: '0.875rem', ...TABULAR }}
              >
                {pct}%
              </Typography>
              <Typography variant="caption" sx={{ color: appColors.text.caption, fontSize: '0.7rem', ...TABULAR }}>
                of {counts.total}
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={pct}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={pct}
              aria-label={`${pct} percent complete, ${counts.accounted} accounted out of ${counts.total} accountability total`}
              sx={{
                height: 6,
                borderRadius: 3,
                bgcolor: appColors.border.default,
                '& .MuiLinearProgress-bar': { bgcolor: getProgressBarColor(opState, pct), borderRadius: 3 },
              }}
            />
          </Box>
        ) : (
          <Typography variant="caption" sx={{ color: appColors.text.placeholder, display: { xs: 'none', sm: 'block' } }}>
            No roster data
          </Typography>
        )}

        <Box sx={{ ml: { xs: 0, sm: 1 }, mt: { xs: 1, sm: 0 }, display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: { xs: 'flex-end', sm: 'flex-start' } }}>
          {onEdit && (
            <Tooltip title="Edit incident details">
              <IconButton
                size="small"
                onClick={(e) => { e.stopPropagation(); onEdit(incident); }}
                sx={{ color: appColors.text.caption }}
              >
                <EditIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          )}
          <IncidentStatusChip status={incident.status} />
          <ChevronRightIcon sx={{ color: appColors.border.hover, fontSize: 20 }} />
        </Box>
      </Box>
    );
  }

  // ── Active card — 3-column tablet / desktop layout ──
  const needsAttentionBorder = opState === 'needsAttention'
    ? `1px solid rgba(211, 47, 47, 0.20)`
    : `1px solid ${appColors.border.default}`;

  const hoverBorder = opState === 'needsAttention'
    ? 'rgba(211, 47, 47, 0.35)'
    : appColors.border.hover;

  // Chip label — shortened on tablet, full on desktop
  const chipLabelOverride = opState === 'needsAttention' && counts.accounted > 1 && counts.notAccounted !== 0;
  const chipLabel = config
    ? compactLabels
      ? (chipLabelOverride ? 'In Progress' : config.shortLabel)
      : (chipLabelOverride ? 'Counting in Progress' : config.label)
    : '';

  return (
    <Box
      onClick={handleCardClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleCardClick(); } }}
      data-testid="incident-row"
      data-op-state={opState}
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr',
          md: 'minmax(0, 1.6fr) minmax(100px, 0.45fr) minmax(170px, 0.75fr)',
        },
        gridTemplateAreas: {
          xs: `"summary" "metrics" "actions"`,
          md: `"summary metrics actions"`,
        },
        columnGap: 2,
        rowGap: { xs: 1.5, md: 0 },
        alignItems: 'start',
        bgcolor: appColors.surface.card,
        borderRadius: '12px',
        border: needsAttentionBorder,
        p: 2,
        mb: 1,
        cursor: 'pointer',
        boxSizing: 'border-box',
        overflow: 'hidden',
        transition: 'box-shadow 0.15s, border-color 0.15s',
        '&:hover': {
          boxShadow: '0 2px 12px rgba(0,0,0,0.07)',
          borderColor: hoverBorder,
        },
        '&:focus-visible': { outline: `2px solid ${appColors.primary.main}`, outlineOffset: 2 },
        minWidth: 0,
      }}
    >
      {/* ── Column 1: Incident identity + progress ── */}
      <Box sx={{ gridArea: 'summary', minWidth: 0 }}>
        {/* Icon + Title + Location */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, minWidth: 0 }}>
          <Box sx={{ flexShrink: 0, pt: 0.25 }}>
            <IncidentIcon incidentType={incident.incidentType} />
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap', minWidth: 0 }}>
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 800,
                  fontSize: '1.05rem',
                  lineHeight: 1.3,
                  color: appColors.text.heading,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  minWidth: 0,
                  maxWidth: '100%',
                }}
              >
                {incident.incidentName}
              </Typography>
              {incident.location && (
                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                  <LocationIcon sx={{ fontSize: 14, color: appColors.text.caption }} />
                  <Typography variant="body2" sx={{ color: appColors.text.caption, whiteSpace: 'nowrap', fontSize: '0.85rem' }}>
                    {incident.location}
                  </Typography>
                </Box>
              )}
            </Box>
            {/* Started date */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
              <CalendarIcon sx={{ fontSize: 13, color: appColors.text.placeholder }} />
              <Typography variant="caption" sx={{ color: appColors.text.placeholder, fontSize: '0.78rem' }}>
                {dateLabel}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Not-accounted summary line */}
        <SummaryLine counts={counts} opState={opState} />

        {/* Team summary */}
        {hasTeam && (
          <Box sx={{ mt: 0.5 }}>
            <HeadcountTeamSummary
              incidentCommanders={incident.incidentCommanders}
              headcounters={incident.headcounters}
              variant="compact"
            />
          </Box>
        )}

        {/* Accountability progress */}
        <Box sx={{ mt: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.25 }}>
            <Typography variant="caption" sx={{ color: appColors.text.caption, fontWeight: 600, fontSize: '0.78rem' }}>
              Accountability
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
              <Typography
                sx={{ fontWeight: 700, color: progressColor, fontSize: '0.875rem', lineHeight: 1.3, ...TABULAR }}
                aria-label={`${pct} percent complete`}
              >
                {pct}%
              </Typography>
              <Typography variant="caption" sx={{ color: appColors.text.caption, fontSize: '0.7rem', ...TABULAR }}>
                of {counts.total}
              </Typography>
            </Box>
          </Box>
          <LinearProgress
            variant="determinate"
            value={pct}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={pct}
            aria-label={`${pct} percent complete, ${counts.accounted} accounted out of ${counts.total} accountability total`}
            sx={{
              height: 6,
              borderRadius: 3,
              bgcolor: appColors.border.default,
              '& .MuiLinearProgress-bar': { bgcolor: progressColor, borderRadius: 3 },
            }}
          />
          {(counts.fullRosterTotal > counts.total || counts.assignedOfficeNoBadge > 0) && (
            <Typography variant="caption" sx={{ color: appColors.text.placeholder, fontSize: '0.72rem', mt: 0.25, display: 'block', ...TABULAR }}>
              Full Roster {counts.fullRosterTotal}
            </Typography>
          )}
        </Box>
      </Box>

      {/* ── Column 2: Core metrics stacked ── */}
      <Box
        sx={{
          gridArea: 'metrics',
          minWidth: 0,
          maxWidth: { md: 140 },
          display: 'flex',
          flexDirection: { xs: 'row', md: 'column' },
          gap: { xs: 3, md: 2 },
          alignSelf: { md: 'center' },
          py: { md: 1 },
        }}
      >
        <Box sx={{ textAlign: { xs: 'left', md: 'center' } }}>
          <Typography
            variant="subtitle2"
            sx={{ color: appColors.text.caption, fontWeight: 700, fontSize: '0.82rem', lineHeight: 1.2, mb: 0.25 }}
          >
            Accounted
          </Typography>
          <Typography sx={{ fontWeight: 800, color: appColors.status.success, fontSize: '1.25rem', lineHeight: 1.1, ...TABULAR }}>
            {counts.accounted}
          </Typography>
        </Box>
        <Box sx={{ textAlign: { xs: 'left', md: 'center' } }}>
          <Typography
            variant="subtitle2"
            sx={{ color: appColors.text.caption, fontWeight: 700, fontSize: '0.82rem', lineHeight: 1.2, mb: 0.25 }}
          >
            Not Accounted
          </Typography>
          <Typography sx={{ fontWeight: 800, color: notAccountedColor, fontSize: '1.25rem', lineHeight: 1.1, ...TABULAR }}>
            {counts.notAccounted}
          </Typography>
        </Box>
      </Box>

      {/* ── Column 3: Timer / status + primary action ── */}
      <Box sx={{ gridArea: 'actions', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {/* Timer + Status chips */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          {now != null && <IncidentElapsedTimer incident={incident} now={now} />}
          {config && (
            <Chip
              label={chipLabel}
              size="small"
              variant="outlined"
              sx={{
                fontWeight: 600,
                fontSize: '0.72rem',
                height: 24,
                borderRadius: '12px',
                bgcolor: config.chipBg,
                color: config.chipColor,
                borderColor: config.chipBorder,
              }}
            />
          )}
        </Box>

        {/* Primary action button */}
        {config && canSeeAction && (
          <Button
            variant={opState === 'needsAttention' ? 'contained' : 'outlined'}
            size="medium"
            onClick={handleAction}
            fullWidth
            aria-label={`${config.actionLabel} for ${incident.incidentName}`}
            sx={{
              fontSize: '0.88rem',
              fontWeight: 700,
              whiteSpace: 'nowrap',
              height: 44,
              borderRadius: '8px',
              ...(opState === 'needsAttention' ? {
                bgcolor: appColors.status.errorDark,
                color: '#fff',
                '&:hover': { bgcolor: '#9A2323' },
              } : {}),
            }}
          >
            {config.actionLabel}
          </Button>
        )}

        {/* Subtle chevron affordance */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mt: -0.5, gap: 0.5 }}>
          {onEdit && (
            <Tooltip title="Edit incident details">
              <IconButton
                size="small"
                onClick={(e) => { e.stopPropagation(); onEdit(incident); }}
                sx={{ color: appColors.text.caption }}
              >
                <EditIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          )}
          <ChevronRightIcon sx={{ color: appColors.border.hover, fontSize: 20, opacity: 0.5 }} />
        </Box>
      </Box>
    </Box>
  );
};

export default IncidentRow;
